import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BRAND, BASE, GREY, BORDER, PRIMARY, SUCCESS } from "../../utils/constants/colors";
import { useTour } from "../../context/TourContext";
import { updateTourProgressAction } from "../../redux/actions/guidedTourActions";

const TOUR_DISPLAY_NAMES = {
  prospects: "Prospects",
  marketing_list: "Marketing List",
  campaigns: "Campaigns",
  dashboard: "Dashboard",
};

export function TourTooltip({
  backProps,
  continuous,
  index,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
  size,
}) {
  const hideNext = step.hideNextButton;
  const { canAdvance, activeTourKey, nextPipelineTour, abortPipeline } = useTour();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tours = useSelector((state) => state.tourProgress?.data?.data?.tours ?? []);

  const showNext = continuous && !hideNext && canAdvance;
  const tourName =
    TOUR_DISPLAY_NAMES[activeTourKey] ||
    (activeTourKey
      ? activeTourKey.charAt(0).toUpperCase() + activeTourKey.slice(1)
      : "Tour");
  const progressPct = Math.round(((index + 1) / size) * 100);

  // Find the active tour by key — checks top-level tours and sub_tours
  const findTourByKey = (list, key) => {
    for (const t of list) {
      if (t.key === key) return t;
      if (t.sub_tours) {
        for (const sub of t.sub_tours) {
          if (sub.key === key) return sub;
        }
      }
    }
    return null;
  };
  const activeTour = findTourByKey(tours, activeTourKey);

  const handleNext = (e) => {
    if (activeTour) {
      dispatch(
        updateTourProgressAction(navigate, {
          tour_id: activeTour.id,
          version: activeTour.version,
          completed_step: index + 1,
        })
      );
    }
    primaryProps.onClick(e);
  };

  return (
    <div
      {...tooltipProps}
      style={{
        background: BASE.WHITE,
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
        padding: "16px 20px 14px",
        maxWidth: step.tooltipMaxWidth ?? 320,
        minWidth: 270,
        fontFamily: "inherit",
      }}
    >
      {/* Header: tour name | step badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: BRAND.PRIMARY,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: GREY.PRIMARY,
              textTransform: "uppercase",
              letterSpacing: 0.7,
            }}
          >
            {tourName} Tour
          </span>
        </div>
        <span
          style={{
            background: PRIMARY.W_100,
            color: BRAND.PRIMARY,
            border: `1px solid ${PRIMARY.W_200}`,
            borderRadius: 20,
            padding: "2px 10px",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          Step {index + 1} of {size}
        </span>
      </div>

      {/* Segmented progress bar + percentage */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div style={{ flex: 1, display: "flex", gap: 3, alignItems: "center" }}>
          {Array.from({ length: size }).map((_, i) => (
            <div
              key={i}
              style={{
                flex: i === index ? 1.6 : 1,
                height: i === index ? 5 : 4,
                borderRadius: 3,
                background:
                  i < index
                    ? SUCCESS.W_500
                    : i === index
                      ? BRAND.PRIMARY
                      : BORDER.DEFAULT,
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: GREY.TERTIARY,
            minWidth: 28,
            textAlign: "right",
          }}
        >
          {progressPct}%
        </span>
      </div>

      {/* Divider below header */}
      <div style={{ height: 2, background: BORDER.DARK, marginBottom: 14 }} />

      {/* Title + body with slight x padding */}
      <div style={{ paddingLeft: 2, paddingRight: 2 }}>
        {step.title && (
          <h4
            style={{
              margin: "0 0 8px",
              fontSize: 14,
              fontWeight: 700,
              color: GREY.PRIMARY,
              lineHeight: 1.35,
            }}
          >
            {step.title}
          </h4>
        )}
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.65,
            color: GREY.QUATINARY,
            marginBottom: 16,
          }}
        >
          {step.content}
        </div>
      </div>

      {/* Divider above footer */}
      <div style={{ height: 2, background: BORDER.DARK, marginBottom: 12 }} />

      {/* Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <button
          {...skipProps}
          onClick={(e) => {
            abortPipeline();
            skipProps.onClick(e);
          }}
          style={{
            background: "none",
            border: "none",
            color: GREY.SECONDARY,
            fontSize: 12,
            cursor: "pointer",
            padding: 0,
            fontFamily: "inherit",
          }}
        >
          Skip tour
        </button>

        <div style={{ display: "flex", gap: 8 }}>
          {index > 0 && !step.hideBackButton && (
            <button
              {...backProps}
              style={{
                background: BASE.WHITE,
                border: `1px solid ${BORDER.DARK}`,
                borderRadius: 8,
                color: GREY.PRIMARY,
                cursor: "pointer",
                padding: "6px 16px",
                fontSize: 12,
                fontWeight: 500,
                fontFamily: "inherit",
              }}
            >
              Back
            </button>
          )}
          {continuous && !hideNext && (
            <button
              {...primaryProps}
              onClick={handleNext}
              aria-hidden={!showNext}
              style={{
                background: BRAND.PRIMARY,
                border: "none",
                borderRadius: 8,
                color: BASE.WHITE,
                cursor: showNext ? "pointer" : "default",
                padding: "6px 18px",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "inherit",
                opacity: showNext ? 1 : 0,
                pointerEvents: showNext ? "auto" : "none",
                transition: "opacity 0.18s ease",
              }}
            >
              {index === size - 1
                ? nextPipelineTour
                  ? `Next: ${nextPipelineTour.name}`
                  : "Finish Tour"
                : "Next"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
